/**
 * Opt-in module access (VP-123).
 *
 * `users.allowed_modules` semantics: null = full access to all STANDARD
 * modules, array = only the listed modules are visible. Opt-in (beta) modules
 * extend this: they are NEVER granted implicitly by null — the module id must
 * be explicitly present in the array (or the user is super_admin).
 *
 * Shared between the backend middleware and the client gating (sidebar,
 * routes) so there is exactly one source of truth instead of per-file email
 * whitelists that drift apart.
 */

export const SITES_MODULE_ID = "sites";

/** Module ids that require an explicit allowed_modules entry (beta features). */
export const OPT_IN_MODULE_IDS: ReadonlySet<string> = new Set([SITES_MODULE_ID]);

/**
 * Wildcard entry meaning "all standard modules". Needed because granting an
 * opt-in module forces allowed_modules to be an array — without the wildcard
 * a full-access user granted 'sites' would be pinned to an explicit module
 * list and miss every standard module added later. `['*', 'sites']` keeps the
 * "full standard access" semantics while carrying the opt-in grant.
 */
export const ALL_STANDARD_MODULES_WILDCARD = "*";

export type ModuleAccessUser = {
  role?: string | null;
  allowedModules?: string[] | null;
};

/** Access to the "Strony" (site-hosting) module: super_admin or explicit opt-in. */
export function canAccessSitesModule(user: ModuleAccessUser | null | undefined): boolean {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return Array.isArray(user.allowedModules) && user.allowedModules.includes(SITES_MODULE_ID);
}

/**
 * Strips opt-in module ids before applying the standard restriction semantics
 * ("null = everything, array = only listed"). Without this, granting a user
 * `['sites']` would accidentally hide every standard module from them.
 * Returns null when nothing restrictive remains.
 */
export function standardAllowedModules(
  allowedModules: string[] | null | undefined
): string[] | null {
  if (!Array.isArray(allowedModules)) return null;
  const filtered = allowedModules.filter((moduleId) => !OPT_IN_MODULE_IDS.has(moduleId));
  if (filtered.includes(ALL_STANDARD_MODULES_WILDCARD)) return null;
  if (filtered.length === allowedModules.length) return allowedModules;
  return filtered.length > 0 ? filtered : null;
}
